import { useState } from 'react';
import { History, Plus, RefreshCw } from 'lucide-react';
import {
  useCreateCreativeTemplateExampleMutation,
  useCreateCreativeTemplateMutation,
  useCreateVerticalProfileMutation,
  useGetCreativeTemplateExampleVersionsQuery,
  useGetCreativeTemplateExampleVersionQuery,
  useGetCreativeTemplateExamplesQuery,
  useGetCreativeTemplateVersionsQuery,
  useGetCreativeTemplateVersionQuery,
  useGetCreativeTemplatesQuery,
  useGetDesignerAgentConfigQuery,
  useGetVerticalProfileVersionsQuery,
  useGetVerticalProfileVersionQuery,
  useGetVerticalProfilesQuery,
  useUpdateCreativeTemplateExampleMutation,
  useUpdateCreativeTemplateMutation,
  useUpdateVerticalProfileMutation,
} from 'src/redux/operations/operations.endpoints';
import type { CreativeTemplateExample, CreativeTemplateExampleWritePayload } from '../types/creative-template-example.types';
import type { CreativeTemplate, CreativeTemplateWritePayload } from '../types/creative-template.types';
import type { VerticalProfile, VerticalProfileWritePayload } from '../types/vertical-profile.types';
import {
  buildCreativeTemplateExamplePayload,
  parseCreativeTemplateExampleMutationError,
} from '../helpers/creative-template-example.helpers';
import {
  buildCreativeTemplatePayload,
  parseCreativeTemplateMutationError,
} from '../helpers/creative-template.helpers';
import {
  buildVerticalProfilePayload,
  parseVerticalProfileMutationError,
} from '../helpers/vertical-profile.helpers';
import { CreativeTemplateExampleList } from '../components/creative-template-example-list';
import { CreativeTemplateExampleSheet } from '../components/creative-template-example-sheet';
import { CreativeTemplateList } from '../components/creative-template-list';
import { CreativeTemplateSheet } from '../components/creative-template-sheet';
import { SchedulerDsButton } from '../components/scheduler-ds-button';
import { VersionHistoryPanel } from '../components/version-history-panel';
import { VerticalProfileList } from '../components/vertical-profile-list';
import { VerticalProfileSheet, type EntitySheetMode } from '../components/vertical-profile-sheet';
import { DesignerExemplarsTab } from './designer-exemplars.tab';

type EditorSubTab = 'profiles' | 'templates' | 'examples' | 'exemplars';

type SheetState<T> =
  | { open: false }
  | { open: true; mode: EntitySheetMode; item: T | null };

function PayloadPreview({ payload }: { payload: unknown }) {
  return (
    <pre className="max-h-96 overflow-auto rounded-md border border-gray-300 bg-white p-3 text-xs text-gray-800">
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
}

const CreativeTemplatesDashboardTab = () => {
  const [subTab, setSubTab] = useState<EditorSubTab>('profiles');
  const [profileSheet, setProfileSheet] = useState<SheetState<VerticalProfile>>({ open: false });
  const [templateSheet, setTemplateSheet] = useState<SheetState<CreativeTemplate>>({ open: false });
  const [exampleSheet, setExampleSheet] = useState<SheetState<CreativeTemplateExample>>({ open: false });
  const [profileSubmitError, setProfileSubmitError] = useState<string | null>(null);
  const [templateSubmitError, setTemplateSubmitError] = useState<string | null>(null);
  const [exampleSubmitError, setExampleSubmitError] = useState<string | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedExampleId, setSelectedExampleId] = useState<string | null>(null);
  const [profileVersion, setProfileVersion] = useState<number | null>(null);
  const [templateVersion, setTemplateVersion] = useState<number | null>(null);
  const [exampleVersion, setExampleVersion] = useState<number | null>(null);

  const {
    data: profiles,
    isLoading: profilesLoading,
    isError: profilesError,
    refetch: refetchProfiles,
    isFetching: profilesFetching,
  } = useGetVerticalProfilesQuery();
  const {
    data: templates,
    isLoading: templatesLoading,
    isError: templatesError,
    refetch: refetchTemplates,
    isFetching: templatesFetching,
  } = useGetCreativeTemplatesQuery();
  const {
    data: examples,
    isLoading: examplesLoading,
    isError: examplesError,
    refetch: refetchExamples,
    isFetching: examplesFetching,
  } = useGetCreativeTemplateExamplesQuery();
  const { data: designerAgentConfig } = useGetDesignerAgentConfigQuery();

  const [createProfile, { isLoading: isCreatingProfile }] = useCreateVerticalProfileMutation();
  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateVerticalProfileMutation();
  const [createTemplate, { isLoading: isCreatingTemplate }] = useCreateCreativeTemplateMutation();
  const [updateTemplate, { isLoading: isUpdatingTemplate }] = useUpdateCreativeTemplateMutation();
  const [createExample, { isLoading: isCreatingExample }] = useCreateCreativeTemplateExampleMutation();
  const [updateExample, { isLoading: isUpdatingExample }] = useUpdateCreativeTemplateExampleMutation();

  const canEdit = !profilesError && !templatesError && !examplesError;

  const profileVersionsQuery = useGetVerticalProfileVersionsQuery(
    { profileId: selectedProfileId ?? '' },
    { skip: !selectedProfileId },
  );
  const profileVersionDetailQuery = useGetVerticalProfileVersionQuery(
    { profileId: selectedProfileId ?? '', versionNumber: profileVersion ?? 0 },
    { skip: !selectedProfileId || profileVersion === null },
  );
  const templateVersionsQuery = useGetCreativeTemplateVersionsQuery(
    { templateId: selectedTemplateId ?? '' },
    { skip: !selectedTemplateId },
  );
  const templateVersionDetailQuery = useGetCreativeTemplateVersionQuery(
    { templateId: selectedTemplateId ?? '', versionNumber: templateVersion ?? 0 },
    { skip: !selectedTemplateId || templateVersion === null },
  );
  const exampleVersionsQuery = useGetCreativeTemplateExampleVersionsQuery(
    { exampleId: selectedExampleId ?? '' },
    { skip: !selectedExampleId },
  );
  const exampleVersionDetailQuery = useGetCreativeTemplateExampleVersionQuery(
    { exampleId: selectedExampleId ?? '', versionNumber: exampleVersion ?? 0 },
    { skip: !selectedExampleId || exampleVersion === null },
  );

  const handleProfileSubmit = async(values: VerticalProfileWritePayload) => {
    try {
      if (profileSheet.open && profileSheet.mode === 'create') {
        const created = await createProfile(buildVerticalProfilePayload(values)).unwrap();
        setProfileSubmitError(null);
        setProfileSheet({ open: true, mode: 'edit', item: created });
        setSelectedProfileId(created.profile_id);
        return;
      }
      if (profileSheet.open && profileSheet.mode === 'edit' && profileSheet.item) {
        const updated = await updateProfile({
          profileId: profileSheet.item.profile_id,
          body: buildVerticalProfilePayload(values),
        }).unwrap();
        setProfileSubmitError(null);
        setProfileSheet({ open: true, mode: 'edit', item: updated });
        setSelectedProfileId(updated.profile_id);
      }
    } catch (error) {
      setProfileSubmitError(parseVerticalProfileMutationError(error));
    }
  };

  const handleTemplateSubmit = async(values: CreativeTemplateWritePayload) => {
    try {
      if (templateSheet.open && templateSheet.mode === 'create') {
        const created = await createTemplate(buildCreativeTemplatePayload(values)).unwrap();
        setTemplateSubmitError(null);
        setTemplateSheet({ open: true, mode: 'edit', item: created });
        setSelectedTemplateId(created.template_id);
        return;
      }
      if (templateSheet.open && templateSheet.mode === 'edit' && templateSheet.item) {
        const updated = await updateTemplate({
          templateId: templateSheet.item.template_id,
          body: buildCreativeTemplatePayload(values),
        }).unwrap();
        setTemplateSubmitError(null);
        setTemplateSheet({ open: true, mode: 'edit', item: updated });
        setSelectedTemplateId(updated.template_id);
      }
    } catch (error) {
      setTemplateSubmitError(parseCreativeTemplateMutationError(error));
    }
  };

  const handleExampleSubmit = async(values: CreativeTemplateExampleWritePayload) => {
    try {
      if (exampleSheet.open && exampleSheet.mode === 'create') {
        const created = await createExample(buildCreativeTemplateExamplePayload(values)).unwrap();
        setExampleSubmitError(null);
        setExampleSheet({ open: true, mode: 'edit', item: created });
        setSelectedExampleId(created.example_id);
        return;
      }
      if (exampleSheet.open && exampleSheet.mode === 'edit' && exampleSheet.item) {
        const updated = await updateExample({
          exampleId: exampleSheet.item.example_id,
          body: buildCreativeTemplateExamplePayload(values),
        }).unwrap();
        setExampleSubmitError(null);
        setExampleSheet({ open: true, mode: 'edit', item: updated });
        setSelectedExampleId(updated.example_id);
      }
    } catch (error) {
      setExampleSubmitError(parseCreativeTemplateExampleMutationError(error));
    }
  };

  const subTabButtons: Array<{ id: EditorSubTab; label: string }> = [
    { id: 'profiles', label: 'Vertical profiles' },
    { id: 'templates', label: 'Templates' },
    { id: 'examples', label: 'Gallery examples' },
    ...(designerAgentConfig?.designer_agent_enabled
      ? [{ id: 'exemplars' as const, label: 'Exemplars' }]
      : []),
  ];

  const isFetching = profilesFetching || templatesFetching || examplesFetching;

  return (
    <div className="space-y-6 overflow-y-auto">
      <section className="rounded-md border border-gray-400 bg-white p-4 sm:p-5">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-medium text-black">Creative template editor</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage vertical profiles, layout templates, and gallery examples. All edits create append-only versions.
            </p>
          </div>
          <SchedulerDsButton
            type="button"
            variant="secondary"
            disabled={isFetching}
            onClick={() => {
              void refetchProfiles();
              void refetchTemplates();
              void refetchExamples();
            }}
          >
            <RefreshCw className={isFetching ? 'animate-spin' : undefined} />
            Refresh
          </SchedulerDsButton>
        </div>

        <div className="mb-5 flex flex-wrap gap-2 border-b border-gray-300 pb-3">
          {subTabButtons.map((button) => (
            <SchedulerDsButton
              key={button.id}
              type="button"
              variant={subTab === button.id ? 'accent' : 'secondary'}
              size="sm"
              onClick={() => setSubTab(button.id)}
            >
              {button.label}
            </SchedulerDsButton>
          ))}
        </div>

        {subTab === 'profiles' ? (
          <div className="space-y-4">
            {canEdit ? (
              <div className="flex flex-wrap gap-3">
                <SchedulerDsButton
                  type="button"
                  onClick={() => {
                    setProfileSubmitError(null);
                    setProfileSheet({ open: true, mode: 'create', item: null });
                  }}
                >
                  <Plus className="size-4" />
                  New profile
                </SchedulerDsButton>
              </div>
            ) : null}
            <VerticalProfileList
              profiles={profiles}
              isLoading={profilesLoading}
              isError={profilesError}
              onSelect={(profile) => {
                setSelectedProfileId(profile.profile_id);
                if (canEdit) {
                  setProfileSubmitError(null);
                  setProfileSheet({ open: true, mode: 'edit', item: profile });
                }
              }}
            />
            {selectedProfileId ? (
              <section className="rounded-md border border-gray-300 bg-gray-50 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-black">
                  <History className="size-4" />
                  Version history — {selectedProfileId}
                </div>
                <VersionHistoryPanel
                  versions={profileVersionsQuery.data}
                  isLoading={profileVersionsQuery.isLoading}
                  isError={profileVersionsQuery.isError}
                  selectedVersion={profileVersion}
                  onSelectVersion={setProfileVersion}
                  onCloseVersion={() => setProfileVersion(null)}
                  isLoadingDetail={profileVersionDetailQuery.isFetching}
                  versionDetail={
                    profileVersionDetailQuery.data
                      ? <PayloadPreview payload={profileVersionDetailQuery.data.payload} />
                      : null
                  }
                />
              </section>
            ) : null}
          </div>
        ) : null}

        {subTab === 'templates' ? (
          <div className="space-y-4">
            {canEdit ? (
              <SchedulerDsButton
                type="button"
                onClick={() => {
                  setTemplateSubmitError(null);
                  setTemplateSheet({ open: true, mode: 'create', item: null });
                }}
              >
                <Plus className="size-4" />
                New template
              </SchedulerDsButton>
            ) : null}
            <CreativeTemplateList
              templates={templates}
              isLoading={templatesLoading}
              isError={templatesError}
              onSelect={(template) => {
                setSelectedTemplateId(template.template_id);
                if (canEdit) {
                  setTemplateSubmitError(null);
                  setTemplateSheet({ open: true, mode: 'edit', item: template });
                }
              }}
            />
            {selectedTemplateId ? (
              <section className="rounded-md border border-gray-300 bg-gray-50 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-black">
                  <History className="size-4" />
                  Version history — {selectedTemplateId}
                </div>
                <VersionHistoryPanel
                  versions={templateVersionsQuery.data}
                  isLoading={templateVersionsQuery.isLoading}
                  isError={templateVersionsQuery.isError}
                  selectedVersion={templateVersion}
                  onSelectVersion={setTemplateVersion}
                  onCloseVersion={() => setTemplateVersion(null)}
                  isLoadingDetail={templateVersionDetailQuery.isFetching}
                  versionDetail={
                    templateVersionDetailQuery.data
                      ? <PayloadPreview payload={templateVersionDetailQuery.data.payload} />
                      : null
                  }
                />
              </section>
            ) : null}
          </div>
        ) : null}

        {subTab === 'examples' ? (
          <div className="space-y-4">
            {canEdit ? (
              <SchedulerDsButton
                type="button"
                onClick={() => {
                  setExampleSubmitError(null);
                  setExampleSheet({ open: true, mode: 'create', item: null });
                }}
              >
                <Plus className="size-4" />
                New example
              </SchedulerDsButton>
            ) : null}
            <CreativeTemplateExampleList
              examples={examples}
              isLoading={examplesLoading}
              isError={examplesError}
              onSelect={(example) => {
                setSelectedExampleId(example.example_id);
                if (canEdit) {
                  setExampleSubmitError(null);
                  setExampleSheet({ open: true, mode: 'edit', item: example });
                }
              }}
            />
            {selectedExampleId ? (
              <section className="rounded-md border border-gray-300 bg-gray-50 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-black">
                  <History className="size-4" />
                  Version history — {selectedExampleId}
                </div>
                <VersionHistoryPanel
                  versions={exampleVersionsQuery.data}
                  isLoading={exampleVersionsQuery.isLoading}
                  isError={exampleVersionsQuery.isError}
                  selectedVersion={exampleVersion}
                  onSelectVersion={setExampleVersion}
                  onCloseVersion={() => setExampleVersion(null)}
                  isLoadingDetail={exampleVersionDetailQuery.isFetching}
                  versionDetail={
                    exampleVersionDetailQuery.data
                      ? <PayloadPreview payload={exampleVersionDetailQuery.data.payload} />
                      : null
                  }
                />
              </section>
            ) : null}
          </div>
        ) : null}

        {subTab === 'exemplars' ? <DesignerExemplarsTab /> : null}
      </section>

      <VerticalProfileSheet
        open={profileSheet.open}
        mode={profileSheet.open ? profileSheet.mode : 'edit'}
        profile={profileSheet.open ? profileSheet.item : null}
        isSaving={isCreatingProfile || isUpdatingProfile}
        submitError={profileSubmitError}
        onOpenChange={(open) => {
          if (!open) {
            setProfileSheet({ open: false });
          }
        }}
        onSubmit={handleProfileSubmit}
      />

      <CreativeTemplateSheet
        open={templateSheet.open}
        mode={templateSheet.open ? templateSheet.mode : 'edit'}
        template={templateSheet.open ? templateSheet.item : null}
        isSaving={isCreatingTemplate || isUpdatingTemplate}
        submitError={templateSubmitError}
        onOpenChange={(open) => {
          if (!open) {
            setTemplateSheet({ open: false });
          }
        }}
        onSubmit={handleTemplateSubmit}
      />

      <CreativeTemplateExampleSheet
        open={exampleSheet.open}
        mode={exampleSheet.open ? exampleSheet.mode : 'edit'}
        example={exampleSheet.open ? exampleSheet.item : null}
        isSaving={isCreatingExample || isUpdatingExample}
        submitError={exampleSubmitError}
        profileOptions={(profiles ?? []).map((profile) => profile.profile_id)}
        onOpenChange={(open) => {
          if (!open) {
            setExampleSheet({ open: false });
          }
        }}
        onSubmit={handleExampleSubmit}
      />
    </div>
  );
};

export default CreativeTemplatesDashboardTab;
